import { profileOfferRoute, Routes } from '../../../../models/routes';

const PROFILE_OFFERS_RETURN_CONTEXT_KEY = 'profileOffersReturnContext';
const RETURN_CONTEXT_MAX_AGE_MS = 30 * 60 * 1000;

export type ProfileOffersReturnContext = {
    path: string;
    page: number;
    scrollY: number;
    savedAt: number;
};

const isBrowser = () => typeof window !== 'undefined';

const normalizeOffersPath = (url: URL) => {
    url.searchParams.delete('append');
    url.searchParams.delete('page');

    const search = url.searchParams.toString();

    return `${url.pathname}${search ? `?${search}` : ''}`;
};

const currentOffersPath = () => {
    if (!isBrowser()) {
        return Routes.Offers;
    }

    return normalizeOffersPath(new URL(window.location.href));
};

const normalizePage = (page?: number) => {
    if (!page || Number.isNaN(page) || page < 1) {
        return 1;
    }

    return Math.floor(page);
};

const readReturnContext = (): ProfileOffersReturnContext | null => {
    if (!isBrowser()) {
        return null;
    }

    const storedValue = window.sessionStorage.getItem(
        PROFILE_OFFERS_RETURN_CONTEXT_KEY,
    );

    if (!storedValue) {
        return null;
    }

    try {
        const parsedValue = JSON.parse(storedValue);

        if (
            typeof parsedValue?.path !== 'string' ||
            typeof parsedValue?.page !== 'number' ||
            typeof parsedValue?.scrollY !== 'number' ||
            typeof parsedValue?.savedAt !== 'number'
        ) {
            window.sessionStorage.removeItem(PROFILE_OFFERS_RETURN_CONTEXT_KEY);
            return null;
        }

        return {
            ...parsedValue,
            page: normalizePage(parsedValue.page),
        };
    } catch (error) {
        console.error('Error parsing offers return context:', error);
        window.sessionStorage.removeItem(PROFILE_OFFERS_RETURN_CONTEXT_KEY);
        return null;
    }
};

export const saveProfileOffersReturnContext = (page = 1) => {
    if (!isBrowser()) {
        return;
    }

    const context: ProfileOffersReturnContext = {
        path: currentOffersPath(),
        page: normalizePage(page),
        scrollY: window.scrollY,
        savedAt: Date.now(),
    };

    window.sessionStorage.setItem(
        PROFILE_OFFERS_RETURN_CONTEXT_KEY,
        JSON.stringify(context),
    );
};

export const readProfileOffersReturnContext = () => {
    const context = readReturnContext();

    if (!context) {
        return null;
    }

    if (Date.now() - context.savedAt > RETURN_CONTEXT_MAX_AGE_MS) {
        window.sessionStorage.removeItem(PROFILE_OFFERS_RETURN_CONTEXT_KEY);
        return null;
    }

    if (context.path !== currentOffersPath()) {
        return null;
    }

    return context;
};

export const clearProfileOffersReturnContext = () => {
    if (!isBrowser()) {
        return;
    }

    window.sessionStorage.removeItem(PROFILE_OFFERS_RETURN_CONTEXT_KEY);
};

export const replaceCurrentProfileOffersPath = (path: string) => {
    if (!isBrowser()) {
        return;
    }

    window.history.replaceState(window.history.state, '', path);
};

export const restoreProfileOffersReturnContext = (scrollY: number) => {
    if (!isBrowser()) {
        return;
    }

    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
            window.scrollTo({
                top: Math.max(0, scrollY),
                left: 0,
            });
        });
    });
};

export const withProfileOffersReturnTo = (
    path: string,
    returnTo?: string | null,
) => {
    if (!returnTo) {
        return path;
    }

    const searchParams = new URLSearchParams({ return_to: returnTo });

    return `${path}?${searchParams.toString()}`;
};

export const buildProfileOfferEditPath = (offerId: number | string) =>
    withProfileOffersReturnTo(
        `${profileOfferRoute(offerId)}/edit`,
        currentOffersPath(),
    );

export const buildProfileOfferInlineUpdatePath = (
    offerId: number | string,
) => {
    const searchParams = new URLSearchParams({
        return_to: currentOffersPath(),
        inline_edit: '1',
    });

    return `${profileOfferRoute(offerId)}?${searchParams.toString()}`;
};
